import React, { useState, useEffect } from 'react';
import { FiMenu, FiX, FiMoon, FiSun } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { useDarkMode } from '../context/DarkModeContext';

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { isDark, toggleDarkMode } = useDarkMode();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    element?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const menuItems = [
    { label: 'Home', id: 'home' },
    { label: 'About', id: 'about' },
    { label: 'Services', id: 'services' },
    { label: 'Portfolio', id: 'portfolio' },
    { label: 'Contact', id: 'contact' },
  ];

  const navVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: 'easeOut' },
    },
  };

  const menuVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.05,
        duration: 0.3,
      },
    }),
  };

  const mobileMenuVariants = {
    hidden: { opacity: 0, height: 0 },
    visible: {
      opacity: 1,
      height: 'auto',
      transition: { duration: 0.3, ease: 'easeOut' },
    },
    exit: {
      opacity: 0,
      height: 0,
      transition: { duration: 0.2, ease: 'easeIn' },
    },
  };

  return (
    <motion.nav
      className={`fixed w-full z-50 transition-all duration-500 ${
        isScrolled
          ? isDark
            ? 'bg-black/95 backdrop-blur-md shadow-lg border-b border-primary-gold/10'
            : 'bg-white/95 backdrop-blur-md shadow-lg border-b border-primary-gold/10'
          : isDark
            ? 'bg-transparent'
            : 'bg-white/30 backdrop-blur-md'
      }`}
      variants={navVariants}
      initial="hidden"
      animate="visible"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo with Animation */}
          <motion.div
            onClick={() => scrollToSection('home')}
            className="flex items-center cursor-pointer group"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <span
              className={`font-montserrat font-bold text-2xl transition-colors duration-300 ${
                isDark ? 'text-white group-hover:text-primary-gold' : 'text-black group-hover:text-primary-gold'
              }`}
            >
              Media
            </span>
            <span className="font-montserrat font-bold text-2xl text-primary-gold">
              Society
            </span>
          </motion.div>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center space-x-1">
            {menuItems.map((item, i) => (
              <motion.button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`font-poppins font-medium px-3 py-2 rounded-lg transition-all duration-300 relative group ${
                  isDark
                    ? 'text-gray-300 hover:text-primary-gold hover:bg-primary-gold/10'
                    : 'text-gray-700 hover:text-primary-gold hover:bg-primary-gold/10'
                }`}
                variants={menuVariants}
                custom={i}
                whileHover={{ y: -2 }}
              >
                {item.label}
                <motion.span
                  className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-primary-gold to-yellow-500 rounded-full"
                  initial={{ width: 0 }}
                  whileHover={{ width: '100%' }}
                  transition={{ duration: 0.3 }}
                ></motion.span>
              </motion.button>
            ))}
          </div>

          {/* Desktop Controls */}
          <div className="hidden lg:flex items-center gap-3">
            {/* Dark Mode Toggle */}
            <motion.button
              onClick={toggleDarkMode}
              className={`p-2.5 rounded-full transition-all duration-300 ${
                isDark
                  ? 'bg-primary-gold/20 text-primary-gold hover:bg-primary-gold/30'
                  : 'bg-gray-200/50 text-gray-700 hover:bg-gray-300'
              }`}
              whileHover={{ rotate: 20, scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Toggle dark mode"
            >
              {isDark ? <FiSun size={20} /> : <FiMoon size={20} />}
            </motion.button>

            {/* CTA Button */}
            <motion.button
              onClick={() => scrollToSection('contact')}
              className={`px-6 py-3 rounded-lg font-montserrat font-bold transition-all duration-300 ${
                isDark
                  ? 'bg-primary-gold text-black hover:shadow-gold-glow-lg'
                  : 'bg-primary-gold text-black hover:shadow-lg'
              }`}
              whileHover={{
                scale: 1.05,
                y: -3,
              }}
              whileTap={{ scale: 0.95 }}
            >
              Get Free Audit
            </motion.button>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center gap-3">
            {/* Mobile Dark Mode Toggle */}
            <motion.button
              onClick={toggleDarkMode}
              className={`p-2 rounded-full transition-all ${
                isDark
                  ? 'bg-primary-gold/20 text-primary-gold'
                  : 'bg-gray-200 text-gray-700'
              }`}
              whileHover={{ rotate: 20, scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Toggle dark mode"
            >
              {isDark ? <FiSun size={18} /> : <FiMoon size={18} />}
            </motion.button>

            {/* Mobile Menu Toggle */}
            <motion.button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`text-2xl transition-colors ${
                isDark ? 'text-primary-gold' : 'text-gray-800'
              }`}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              {mobileMenuOpen ? <FiX /> : <FiMenu />}
            </motion.button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              className={`lg:hidden border-t transition-colors ${
                isDark
                  ? 'bg-dark-secondary border-primary-gold/20'
                  : 'bg-gray-100 border-gray-200'
              }`}
              variants={mobileMenuVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <div className="py-6 px-4 flex flex-col space-y-2">
                {menuItems.map((item, i) => (
                  <motion.button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`font-poppins text-lg px-4 py-3 rounded-lg transition-all text-left ${
                      isDark
                        ? 'text-gray-300 hover:text-primary-gold hover:bg-primary-gold/10'
                        : 'text-gray-700 hover:text-primary-gold hover:bg-primary-gold/10'
                    }`}
                    variants={menuVariants}
                    custom={i}
                    whileHover={{ x: 10 }}
                  >
                    {item.label}
                  </motion.button>
                ))}
                <motion.button
                  onClick={() => scrollToSection('contact')}
                  className={`w-full mt-4 px-6 py-3 rounded-lg font-montserrat font-bold transition-all ${
                    isDark
                      ? 'bg-primary-gold text-black hover:shadow-gold-glow-lg'
                      : 'bg-primary-gold text-black hover:shadow-lg'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Get Free Audit
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

export default Navbar;
